import type { ColumnSelectorButtonProps } from "@/components-v2/ColumnSelectorDialog";
import type { DropdownMenuProps } from "@/components-v2/Dropdown";
import {
  ColumnSelectorState,
  ColumnSelectorUpdate,
} from "@/components-v2/columnSelectorDialogDomain";
import { DataTableProps, TableColumn, TableColumnValue } from "@/components-v2/DataTable";
import { DataTableSort } from "@/components-v2/dataTableHelpers";
import { DateInputState } from "@/components-v2/DateInput";
import type { FilterButtonProps } from "@/components-v2/FilterDialog";
import {
  decodeFilters,
  encodeFilters,
  FilterDialogState,
  FilterDialogUpdate,
  FilterField,
  FilterGroup,
  filterTag,
  initFilterDialogState,
  toFilterGroup,
} from "@/components-v2/filterDialogDomain";
import {
  DCEvent,
  debounceEvents,
  HttpResponse,
  navigateTo,
  showGeneralError,
  UpdateStoreEvent,
  WithStoreEvent,
} from "@/events/eventsDomain";
import { ExcelCellData, downloadExcelEvent } from "@/excel/excelDomain";
import { PreppedI18nState, prepI18nState } from "@/i18n/i18nDomain";
import { TMessage, TMessageParam } from "@/i18n/tt";
import {
  PlainReportValue,
  portfolioV2ApiClient,
  ReportField,
  ReportFieldFormat,
  ReportRow,
  ReportSearchRequest,
  ReportSearchResult,
  ReportTag,
  ReportValue,
} from "@/portfolio/search/portfolioV2ApiClient";
import { DetailsField } from "@/shareholderRegister/shareholders/shareholderDetailsComponents";
import { parseDateInput, toLocalDateISOString } from "@/utils/Dates";
import { multiValue, QueryParams, singleValue } from "@/utils/queryParams";
import compact from "lodash/compact";
import isEqual from "lodash/isEqual";
import isString from "lodash/isString";

type ReportsSearchParams = { q?: string; date?: string; filterOp?: string; filters?: string[] };

export type PortfolioReportsState = {
  searchParams?: ReportsSearchParams;
  query?: string;
  date?: { parsed: Date | null; input: string; error: boolean };
  filterGroup?: FilterGroup<string>;
  visibleColumns?: string[];
  columnSelector?: ColumnSelectorState<string>;
  filterDialog?: FilterDialogState<string>;
  sort?: DataTableSort;
  fields?: Record<string, ReportField>;
  isLoading?: boolean;
  result?: ReportSearchResult;
  selectedRow?: ReportRow;
};

const toSearchParams = (queryParams: QueryParams): ReportsSearchParams => ({
  q: singleValue(queryParams.q),
  date: singleValue(queryParams.date),
  filterOp: singleValue(queryParams.filterOp),
  filters: multiValue(queryParams.filters),
});

const toSort = (by: string | undefined, dir: string | undefined): DataTableSort | undefined =>
  by ? { by, direction: dir === "asc" ? "asc" : "desc" } : undefined;

export const queryParamsToState = (queryParams: QueryParams): PortfolioReportsState => {
  const searchParams = toSearchParams(queryParams);
  return {
    searchParams,
    query: searchParams.q,
    date: parseDateInput(searchParams.date ?? ""),
    filterGroup: decodeFilters(isString, searchParams.filterOp, searchParams.filters),
    visibleColumns: multiValue(queryParams.cols),
    sort: toSort(singleValue(queryParams.sort), singleValue(queryParams.dir)),
  };
};

const viewToQueryParams = ({ visibleColumns, sort }: PortfolioReportsState): QueryParams => ({
  cols: visibleColumns,
  sort: sort?.by,
  dir: sort?.direction,
});

const stateToQueryParams = (state: PortfolioReportsState): QueryParams => ({
  q: state.query?.trim(),
  date: state.date?.parsed ? toLocalDateISOString(state?.date.parsed) : undefined,
  ...encodeFilters(state.filterGroup),
  ...viewToQueryParams(state),
});

const isSameSearch = (state: PortfolioReportsState | undefined, queryParams: QueryParams) =>
  state?.searchParams !== undefined && isEqual(state.searchParams, toSearchParams(queryParams));

const loadReports = (state: PortfolioReportsState): DCEvent[] => [
  setReportsState({ ...state, isLoading: true }),
  search(state),
];

export const initReportsResultPage = (queryParams: QueryParams): DCEvent[] => [
  {
    type: "with-store",
    events: ({ portfolioReports }) =>
      compact([
        ...(isSameSearch(portfolioReports, queryParams)
          ? []
          : loadReports(queryParamsToState(queryParams))),
        !portfolioReports?.fields && loadFields,
      ]),
  },
];

const updateReportsState = (
  fn: (reports: PortfolioReportsState) => PortfolioReportsState | undefined,
): UpdateStoreEvent => ({
  type: "update-store",
  data: { portfolioReports: (reports = {}) => fn(reports) ?? reports },
});

const setReportsState = (reports: PortfolioReportsState): UpdateStoreEvent =>
  updateReportsState(({ fields }) => ({ ...reports, fields }));

const withReportsState = (fn: (state: PortfolioReportsState) => DCEvent[]): WithStoreEvent => ({
  type: "with-store",
  events: (state) => fn(state.portfolioReports ?? {}),
});

export const setReportsStateField = <K extends keyof PortfolioReportsState>(
  key: K,
  value: PortfolioReportsState[K],
) => updateReportsState((state) => ({ ...state, [key]: value }));

const replaceReportsUrl: DCEvent = {
  type: "with-store",
  events: ({ portfolioReports = {} }) => [
    navigateTo(
      {
        name: "portfolio/reports/result",
        queryParams: { ...portfolioReports.searchParams, ...viewToQueryParams(portfolioReports) },
      },
      { replace: true },
    ),
  ],
};

const loadFields: DCEvent = portfolioV2ApiClient.getFields({
  onSuccess: (res: HttpResponse<Record<string, ReportField>>) => [
    setReportsStateField("fields", res.data),
  ],
  onFailure: () => [showGeneralError()],
});

const isCurrentSearch = (
  reports: PortfolioReportsState,
  { query, filterGroup }: PortfolioReportsState,
) => reports.query === query && isEqual(reports.filterGroup, filterGroup);

const toSearchRequest = (state: PortfolioReportsState): ReportSearchRequest => ({
  query: { text: state.query ?? "" },
  date: state.date?.parsed ? toLocalDateISOString(state.date.parsed) : undefined,
  filter: state.filterGroup,
});

const search = (state: PortfolioReportsState): DCEvent =>
  portfolioV2ApiClient.search(toSearchRequest(state), {
    onSuccess: (res: HttpResponse<ReportSearchResult>) => [
      updateReportsState((reports) =>
        isCurrentSearch(reports, state)
          ? { ...reports, isLoading: false, result: res.data }
          : undefined,
      ),
    ],
    onFailure: () => [
      updateReportsState((reports) =>
        isCurrentSearch(reports, state) ? { ...reports, isLoading: false } : undefined,
      ),
      showGeneralError(),
    ],
  });

export const submitReportSearch = (state: PortfolioReportsState): DCEvent[] => {
  const queryParams = stateToQueryParams(state);
  return [
    ...loadReports(queryParamsToState(queryParams)),
    navigateTo({ name: "portfolio/reports/result", queryParams }),
  ];
};

export const updateQuery = (query: string): DCEvent[] => [
  setReportsStateField("query", query),
  withReportsState((state) => [
    debounceEvents("portfolio-reports/search", 300, submitReportSearch(state), {
      onLeading: [setReportsStateField("isLoading", true)],
    }),
  ]),
];

export const setDate = (date: DateInputState): DCEvent[] => [setReportsStateField("date", date)];

export const setDateAndSubmit = (date: DateInputState): DCEvent[] =>
  compact([
    setReportsStateField("date", date),
    !date.error && withReportsState((state) => submitReportSearch(state)),
  ]);

const shownColumns = (fields: Record<string, ReportField>, visibleColumns: string[] | undefined) =>
  (visibleColumns ?? Object.keys(fields)).filter((name) => name in fields);

export const openReportsColumnSelector: DCEvent[] = [
  updateReportsState((state) =>
    state.fields
      ? {
          ...state,
          columnSelector: {
            columns: Object.keys(state.fields),
            visibleColumns: shownColumns(state.fields, state.visibleColumns),
            defaultColumns: Object.keys(state.fields),
          },
        }
      : undefined,
  ),
];

export const updateReportsColumnSelector: ColumnSelectorUpdate<string> = (f) =>
  updateReportsState((state) =>
    state.columnSelector ? { ...state, columnSelector: f(state.columnSelector) } : undefined,
  );

export const submitReportsColumnSelector: DCEvent[] = [
  updateReportsState((state) =>
    state.columnSelector
      ? {
          ...state,
          visibleColumns: state.columnSelector.visibleColumns,
          columnSelector: undefined,
        }
      : undefined,
  ),
  replaceReportsUrl,
];

export const closeReportsColumnSelector: DCEvent[] = [
  updateReportsState((state) => ({ ...state, columnSelector: undefined })),
];

const reportFieldLabel = ({ label }: ReportField): TMessage =>
  typeof label === "string" ? { translated: label } : label;

const reportFilterField = (id: string, field: ReportField): FilterField<string> => {
  const label = reportFieldLabel(field);
  switch (field.type) {
    case "string":
      return { id, label, type: field.format?.type === "country" ? "country" : "text" };
    case "number":
      return { id, label, type: "number" };
    case "date":
      return { id, label, type: "date" };
  }
};

export const reportFilterFields = (fields: Record<string, ReportField>): FilterField<string>[] =>
  Object.entries(fields).map(([id, field]) => reportFilterField(id, field));

export const openReportsFilterDialog: DCEvent[] = [
  updateReportsState((state) =>
    state.fields
      ? {
          ...state,
          filterDialog: initFilterDialogState(reportFilterFields(state.fields), state.filterGroup),
        }
      : undefined,
  ),
];

export const updateReportsFilterDialog: FilterDialogUpdate<string> = (f) =>
  updateReportsState((state) =>
    state.filterDialog ? { ...state, filterDialog: f(state.filterDialog) } : undefined,
  );

export const submitReportsFilterDialog: DCEvent[] = [
  withReportsState((state) =>
    state.filterDialog
      ? submitReportSearch({ ...state, filterGroup: toFilterGroup(state.filterDialog) })
      : [],
  ),
];

export const closeReportsFilterDialog: DCEvent[] = [
  updateReportsState((state) => ({ ...state, filterDialog: undefined })),
];

export const prepReportsFilterButton = ({
  fields,
  filterGroup,
}: PortfolioReportsState): FilterButtonProps => ({
  tag: fields && filterTag(filterGroup, reportFilterFields(fields)),
  disabled: !fields,
  onClick: openReportsFilterDialog,
});

export const reportsColumnLabel = (
  { fields }: PortfolioReportsState,
  name: string,
): string | TMessage => fields?.[name]?.label ?? name;

export const prepReportsColumnSelectorButton = ({
  fields,
  visibleColumns,
}: PortfolioReportsState): ColumnSelectorButtonProps => ({
  hiddenCount: fields
    ? Object.keys(fields).length - shownColumns(fields, visibleColumns).length
    : 0,
  disabled: !fields,
  onClick: openReportsColumnSelector,
});

const downloadReportExcel: DCEvent = {
  type: "with-store",
  events: ({ portfolioReports = {}, i18n }) => {
    const table = prepReportsTable(portfolioReports);
    const date = portfolioReports.result?.date;
    return table && date
      ? [
          downloadExcelEvent(
            `${prepI18nState(i18n).t({ key: "portfolio.reports.new-report-title" })} ${date}.xlsx`,
            table,
            i18n,
            { onFailure: [showGeneralError()] },
          ),
        ]
      : [];
  },
};

export const prepReportsMenu = ({
  result,
  fields,
  isLoading,
}: PortfolioReportsState): DropdownMenuProps | undefined =>
  result && fields
    ? {
        button: { type: "button", variant: "secondary", label: { key: "options" } },
        items: [
          {
            type: "item",
            icon: "microsoft-excel-logo",
            label: { key: "download" },
            isDisabled: isLoading,
            onSelect: [downloadReportExcel],
          },
        ],
      }
    : undefined;

export const setReportsSort = (sort: DataTableSort): DCEvent[] => [
  setReportsStateField("sort", sort),
  replaceReportsUrl,
];

export const prepReportsTable = ({
  result,
  fields,
  visibleColumns,
  sort,
  selectedRow,
}: PortfolioReportsState): DataTableProps<ReportRow> | undefined =>
  !result || !fields
    ? undefined
    : {
        tableColumns: shownColumns(fields, visibleColumns).map((name) =>
          reportColumn(name, fields[name]),
        ),
        tableData: result.data,
        sort: sort ?? null,
        onSortChange: setReportsSort,
        getId: (row) => row.id,
        onRowClick: (row) => [
          updateReportsState((state) => ({
            ...state,
            selectedRow: row,
          })),
        ],
        rowProps: (row) => (row === selectedRow ? { selected: true } : undefined),
      };

export const closeDetailsPanel: DCEvent = updateReportsState((state) => ({
  ...state,
  selectedRow: undefined,
}));

export const prepReportRowDetailsFields = (
  { fields }: PortfolioReportsState,
  row: ReportRow,
): DetailsField[] =>
  Object.entries(fields ?? {}).map(([name, field]) => [
    reportFieldLabel(field),
    reportCellValue(field, row[name]),
  ]);

const shouldUseFormattedFilterValue = (format: ReportFieldFormat) => {
  switch (format.type) {
    case "org-no":
      return false;
    default:
      return true;
  }
};

const isReportTag = (value: ReportValue | undefined): value is ReportTag =>
  typeof value === "object";

const plainValue = (value: ReportValue | undefined): PlainReportValue | undefined =>
  isReportTag(value) ? value.text : value;

const reportColumnValue = (field: ReportField, value: ReportValue | undefined): TableColumnValue =>
  isReportTag(value)
    ? ["tag", { label: value.text, color: value.color }]
    : reportCellValue(field, value);

const reportColumn = (name: string, field: ReportField): TableColumn<ReportRow> => ({
  name,
  label: field.label,
  align: field.type === "number" ? "right" : "left",
  getValue: (row) => reportColumnValue(field, row[name]),
  getFilterValue: (row) =>
    field.format && shouldUseFormattedFilterValue(field.format)
      ? formattedValue(field.format, plainValue(row[name]))
      : plainValue(row[name]),
  getSortValue: (row) =>
    field.format?.type === "country"
      ? formattedValue(field.format, plainValue(row[name]))
      : plainValue(row[name]),
  exportToExcel: (row, _, i18n) => reportExcelCell(field, plainValue(row[name]), i18n),
});

const reportExcelCell = (
  field: ReportField,
  value: PlainReportValue | undefined,
  i18n: PreppedI18nState,
): ExcelCellData => {
  switch (field.type) {
    case "number":
      return { type: "numeric", value: value === undefined ? undefined : Number(value) };
    case "date":
      return { type: "date", value: value === undefined ? undefined : String(value) };
    case "string":
      return {
        type: "string",
        value: value === undefined ? undefined : i18n.tf(reportCellValue(field, value)),
      };
  }
};

export const reportCellValue = (
  field: ReportField,
  value: ReportValue | undefined,
): TMessageParam | TMessage | PlainReportValue | undefined => {
  const plain = plainValue(value);
  return plain === undefined || !field.format ? plain : formattedValue(field.format, plain);
};

const formattedValue = (
  format: ReportFieldFormat,
  value: PlainReportValue | undefined,
): TMessageParam | undefined => {
  if (value === undefined) {
    return;
  }
  switch (format.type) {
    case "country":
      return { ...format, code: String(value) };
    case "number":
    case "percent":
      return { ...format, value: Number(value) };
    case "org-no":
      return { ...format, value: String(value) };
    case "date":
    case "datetime":
    case "time":
      return { ...format, value };
  }
};
